import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search,
  DollarSign,
  TrendingUp,
  CreditCard,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  RefreshCw,
} from "lucide-react";


// Mock transaction data
const MOCK_TRANSACTIONS = [
  {
    id: "1",
    type: "subscription",
    description: "Essential Subscription - John Doe",
    amount: 1199.23,
    status: "completed",
    date: new Date(),
  },
  {
    id: "2",
    type: "deal_submission",
    description: "Deal Submission Fee - ABC Corp",
    amount: 300,
    status: "completed",
    date: new Date(Date.now() - 86400000),
  },
  {
    id: "3",
    type: "deal_acceptance",
    description: "Deal Acceptance Fee - XYZ Inc",
    amount: 2449,
    status: "pending",
    date: new Date(Date.now() - 172800000),
  },
  {
    id: "4",
    type: "refund",
    description: "Refund - Cancelled Subscription",
    amount: -504,
    status: "completed",
    date: new Date(Date.now() - 259200000),
  },
];

export default function FinancePayments() {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const filteredTransactions = MOCK_TRANSACTIONS.filter((tx) => {
    const matchesSearch = tx.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || tx.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const totalRevenue = MOCK_TRANSACTIONS.filter((t) => t.amount > 0 && t.status === "completed")
    .reduce((sum, t) => sum + t.amount, 0);
  const pendingAmount = MOCK_TRANSACTIONS.filter((t) => t.status === "pending")
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Finance & Payments</h1>
          <p className="text-white/60 mt-1">
            Track revenue, transactions, and payment status
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync Stripe
          </Button>
          <Button className="bg-brand-gold text-brand-navy hover:bg-brand-gold/90">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/20">
                <DollarSign className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Monthly Revenue</p>
                <p className="text-2xl font-bold text-white">
                  ${totalRevenue.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <Receipt className="h-6 w-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Pending</p>
                <p className="text-2xl font-bold text-white">
                  ${pendingAmount.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <CreditCard className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Transactions</p>
                <p className="text-2xl font-bold text-white">
                  {MOCK_TRANSACTIONS.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/20">
                <TrendingUp className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Growth</p>
                <p className="text-2xl font-bold text-white">+12.5%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Placeholder */}
      <Card className="bg-white/10 border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Revenue Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-white/50">
            Revenue chart - Connect to Stripe for live data
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
          />
        </div>
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList className="bg-white/10">
            <TabsTrigger value="all" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
              All
            </TabsTrigger>
            <TabsTrigger value="subscription" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="deal_submission" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
              Deal Fees
            </TabsTrigger>
            <TabsTrigger value="refund" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
              Refunds
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Transactions List */}
      <Card className="bg-white/10 border-white/20">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                    Transaction
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                    Type
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-white/60">
                    Date
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-white/60">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-white">{tx.description}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-white border-white/30 capitalize">
                        {tx.type.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant="outline"
                        className={
                          tx.status === "completed"
                            ? "bg-green-500/20 text-green-400 border-green-500/50"
                            : "bg-yellow-500/20 text-yellow-400 border-yellow-500/50"
                        }
                      >
                        {tx.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-white/60">
                      {tx.date.toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {tx.amount > 0 ? (
                          <ArrowUpRight className="h-4 w-4 text-green-400" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-400" />
                        )}
                        <span className={tx.amount > 0 ? "text-green-400" : "text-red-400"}>
                          ${Math.abs(tx.amount).toLocaleString()}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
